/**
 * Admin "act-as" impersonation state (SUPPORT-IMPERSONATION).
 *
 * `enter` starts a session: swap the in-memory access token to the act-as
 * token, flip the client's impersonating flag (so 401-refresh routes to
 * `/impersonation/refresh`), and re-fetch `/me` — the whole app then
 * authorizes and renders as the assumed user. `exit` pops one level:
 * back to the parent tenant_admin session, or all the way to the
 * superadmin's own session (restored via `/auth/refresh`).
 *
 * The chain drives the banner (`ImpersonationBanner`) — one entry per
 * level: `Platform admin → Tenant admin · Acme → Participant · Jane`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import * as authApi from '../api/auth';
import { setAccessToken, setImpersonating } from '../api/client';
import * as impApi from '../api/impersonation';
import { useAuth } from './AuthContext';

export interface ImpersonationLevel {
  role: 'tenant_admin' | 'participant';
  /** Display name (or email) of the assumed user. */
  label: string;
  tenantName: string | null;
  readOnly: boolean;
}

export interface EnterParams {
  targetUserId: string;
  /** Only for a top-level (tenant_admin) start. */
  tenantId?: string;
  readOnly: boolean;
  reason: string;
  level: ImpersonationLevel;
}

interface ImpersonationContextValue {
  chain: ImpersonationLevel[];
  isImpersonating: boolean;
  /** True once the newest level is read-only (any level being read-only
   *  forces the whole session read-only server-side, so this is the tip). */
  readOnly: boolean;
  enter(params: EnterParams): Promise<void>;
  exit(): Promise<void>;
}

// A safe no-op default so components that embed impersonation controls
// (e.g. the "Start support session" dialog) render fine even where the
// provider isn't mounted — tests, isolated stories. The real provider in
// main.tsx always overrides this in the running app.
const DEFAULT_VALUE: ImpersonationContextValue = {
  chain: [],
  isImpersonating: false,
  readOnly: false,
  enter: async () => {},
  exit: async () => {},
};

const ImpersonationContext =
  createContext<ImpersonationContextValue>(DEFAULT_VALUE);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [chain, setChain] = useState<ImpersonationLevel[]>([]);

  const enter = useCallback(
    async ({ targetUserId, tenantId, readOnly, reason, level }: EnterParams) => {
      const resp = await impApi.startImpersonation({
        target_user_id: targetUserId,
        tenant_id: tenantId,
        read_only: readOnly,
        reason,
      });
      setAccessToken(resp.access_token);
      setImpersonating(true);
      await auth.refresh(); // re-fetch /me as the assumed user
      setChain((c) => [...c, level]);
    },
    [auth],
  );

  const exit = useCallback(async () => {
    const resp = await impApi.exitImpersonation();
    if (resp.restored === 'parent' && resp.access_token) {
      // Popped to the parent tenant_admin session — still impersonating.
      setAccessToken(resp.access_token);
      await auth.refresh();
      setChain((c) => c.slice(0, -1));
      return;
    }
    // Popped to the root — restore the superadmin's own session. The
    // superadmin /auth refresh cookie was never overwritten, so this just
    // re-mints their access token.
    setImpersonating(false);
    await authApi.refresh();
    await auth.refresh();
    setChain([]);
  }, [auth]);

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      chain,
      isImpersonating: chain.length > 0,
      readOnly: chain.length > 0 ? chain[chain.length - 1].readOnly : false,
      enter,
      exit,
    }),
    [chain, enter, exit],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationContextValue {
  return useContext(ImpersonationContext);
}
